import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, Stethoscope, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/services/api';

export default function GestionTypesVisites() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    libelle: '',
    duree_validite_mois: 24
  });

  useEffect(() => {
    loadTypes();
  }, []);

  const loadTypes = async () => {
    try {
      setLoading(true);
      const data = await api.getTypesVisites();
      setTypes(data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des types de visites');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (type) => {
    setEditingType(type);
    setFormData({
      code: type.code,
      libelle: type.libelle,
      duree_validite_mois: type.duree_validite_mois || 24
    });
    setIsDialogOpen(true);
  };

  const handleNew = () => {
    setEditingType(null);
    setFormData({
      code: '',
      libelle: '',
      duree_validite_mois: 24
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.code || !formData.libelle) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    try {
      if (editingType) {
        await api.updateTypeVisite(editingType.id, formData);
        toast.success('Type de visite modifié');
      } else {
        await api.createTypeVisite(formData);
        toast.success('Type de visite créé');
      }
      setIsDialogOpen(false);
      loadTypes();
    } catch (error) {
      toast.error(error.message || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (type) => {
    if (!window.confirm(`Supprimer le type "${type.libelle}" ?`)) return;
    
    try {
      await api.deleteTypeVisite(type.id);
      toast.success('Type de visite supprimé');
      loadTypes();
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Stethoscope className="h-6 w-6 text-primary" />
            Types de visites médicales
          </h1>
          <p className="text-gray-600 mt-1">Gérer les types de visites médicales</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadTypes}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
          <Button onClick={handleNew}>
            <Plus className="h-4 w-4 mr-2" />
            Nouveau type
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Liste des types de visites</CardTitle>
          <CardDescription>{types.length} types configurés</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Libellé</TableHead>
                <TableHead>Validité</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {types.map((type) => (
                <TableRow key={type.id}>
                  <TableCell className="font-mono font-medium">{type.code}</TableCell>
                  <TableCell>{type.libelle}</TableCell>
                  <TableCell>{type.duree_validite_mois || 24} mois</TableCell>
                  <TableCell>
                    <Badge variant={type.actif !== false ? "default" : "secondary"}>
                      {type.actif !== false ? "Actif" : "Inactif"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(type)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(type)} className="text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingType ? 'Modifier' : 'Nouveau'} type de visite</DialogTitle>
            <DialogDescription>
              {editingType ? 'Modifier les informations du type' : 'Créer un nouveau type de visite médicale'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Code *</Label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="Ex: VM-INIT, VM-PERI"
              />
            </div>
            <div className="space-y-2">
              <Label>Libellé *</Label>
              <Input
                value={formData.libelle}
                onChange={(e) => setFormData({ ...formData, libelle: e.target.value })}
                placeholder="Ex: Visite médicale initiale"
              />
            </div>
            <div className="space-y-2">
              <Label>Durée de validité (mois)</Label>
              <Input
                type="number"
                value={formData.duree_validite_mois}
                onChange={(e) => setFormData({ ...formData, duree_validite_mois: parseInt(e.target.value) || 24 })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleSave}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
